import React, { useEffect, useState } from "react";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { devLog } from "@/lib/devLog";
import { useSmartSuggestions } from "@/hooks/useSmartSuggestions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { UploadSimple, MagicWand, Warning, Paperclip, ArrowRight, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import PurchaseHeaderFields from "@/components/purchases/PurchaseHeaderFields";
import PurchaseLinesTable, { PurchaseUnitDatalist } from "@/components/purchases/PurchaseLinesTable";

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

/**
 * PurchaseUploadSheet — Tally-style purchase bill upload with FULL multi-line
 * extraction. Every line item is preserved separately with its own HSN, qty,
 * rate and GST %. The original PDF is attached to the created purchase row.
 */
export function PurchaseUploadSheet({ projects, onUploaded, open: openProp, onOpenChange, hideTrigger }) {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp !== undefined ? openProp : openInternal;
  const setOpen = onOpenChange || setOpenInternal;

  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [interstate, setInterstate] = useState(false);
  // Destination routing — where to add these items as stock, if anywhere
  const [destination, setDestination] = useState("none"); // "none" | "warehouse" | "site"
  const [warehouseId, setWarehouseId] = useState("");
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/warehouses");
        setWarehouses((data || []).filter((w) => w.active !== false));
      } catch (e) { devLog.warn("warehouses fetch failed", e); }
    })();
  }, [open]);

  const reset = () => {
    setFile(null); setParsing(false); setSaving(false); setForm(null);
    setProjectId(""); setInterstate(false);
    setDestination("none"); setWarehouseId("");
  };

  const parse = async (f) => {
    if (!f) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/purchases/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setForm({
        vendor: data.vendor || "",
        vendor_gstin: data.vendor_gstin || "",
        invoice_number: data.invoice_number || "",
        date: data.date || new Date().toISOString().slice(0, 10),
        notes: data.notes || "",
        freight_amount: Number(data.freight_amount || 0),
        insurance_amount: Number(data.insurance_amount || 0),
        packing_amount: Number(data.packing_amount || 0),
        other_charges_amount: Number(data.other_charges_amount || 0),
        items: (data.items || []).length ? data.items.map((it) => ({ ...it, unit: it.unit || "nos" })) : [{ description: "", hsn: "", qty: 1, unit: "nos", unit_price: 0, gst_rate: 18 }],
      });
      const extras = Number(data.freight_amount || 0) + Number(data.insurance_amount || 0) + Number(data.packing_amount || 0) + Number(data.other_charges_amount || 0);
      toast.success(`Extracted ${data.line_count || 0} line${data.line_count === 1 ? "" : "s"} from ${f.name}${extras ? ` · +₹${extras} in freight/other charges` : ""}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Parse failed");
      // Even on failure open a blank form so user can key items manually
      setForm({
        vendor: "", vendor_gstin: "", invoice_number: "",
        date: new Date().toISOString().slice(0, 10), notes: "",
        freight_amount: 0, insurance_amount: 0, packing_amount: 0, other_charges_amount: 0,
        items: [{ description: "", hsn: "", qty: 1, unit: "nos", unit_price: 0, gst_rate: 18 }],
      });
    } finally {
      setParsing(false);
    }
  };

  const updateItem = (idx, key, value) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)),
    }));
  };
  const removeItem = (idx) => {
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };
  const addItem = () => {
    setForm((f) => ({
      ...f,
      items: [...(f.items || []), { description: "", hsn: "", qty: 1, unit: "nos", unit_price: 0, gst_rate: 18 }],
    }));
  };

  // Totals with per-line GST split
  const items = form?.items || [];
  const subtotal = items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0), 0);
  const totalTax = items.reduce(
    (s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0) * Number(it.gst_rate || 0) / 100,
    0,
  );
  const cgst = interstate ? 0 : totalTax / 2;
  const sgst = interstate ? 0 : totalTax / 2;
  const igst = interstate ? totalTax : 0;
  const grand = subtotal + totalTax;

  // Smart suggestions — fires whenever the vendor name changes
  const { suggestions } = useSmartSuggestions("po", form?.vendor || "");
  const applySuggestions = () => {
    if (!suggestions?.length) return;
    setForm((f) => ({
      ...f,
      items: suggestions.map((s) => ({
        description: s.description,
        hsn: s.hsn || "",
        qty: s.qty || 1,
        unit: s.unit || "nos",
        unit_price: s.unit_price || 0,
        gst_rate: s.gst_rate || 18,
      })),
    }));
    toast.success(`Prefilled ${suggestions.length} item${suggestions.length === 1 ? "" : "s"} from last bill of this vendor`);
  };

  const save = async () => {
    if (!form?.vendor) return toast.error("Vendor name is required");
    if (!form.items?.length) return toast.error("At least one line item is required");
    if (destination === "warehouse" && !warehouseId) return toast.error("Select a warehouse — where the goods are received");
    if (destination === "site" && !projectId) return toast.error("Select a site (project) — where the goods are delivered");
    setSaving(true);
    try {
      const cleanItems = form.items
        .filter((it) => (it.description || "").trim())
        .map((it) => {
          const qty = Math.max(Number(it.qty) || 0, 0);
          return {
            description: it.description,
            hsn: it.hsn || "",
            sku: it.sku || "",
            unit: it.unit || "nos",
            qty: qty > 0 ? qty : 1,
            unit_price: Math.max(Number(it.unit_price) || 0, 0),
            gst_rate: Math.max(Number(it.gst_rate) || 0, 0),
          };
        });
      if (!cleanItems.length) {
        toast.error("Please fill at least one item description");
        setSaving(false);
        return;
      }
      const { data: created } = await api.post("/purchases", {
        vendor: form.vendor,
        vendor_gstin: form.vendor_gstin || "",
        invoice_number: form.invoice_number || "",
        project_id: destination === "site" ? projectId : (projectId || null),
        date: form.date,
        notes: form.notes || "",
        is_interstate: interstate,
        items: cleanItems,
        freight_amount: Number(form.freight_amount || 0),
        insurance_amount: Number(form.insurance_amount || 0),
        packing_amount: Number(form.packing_amount || 0),
        other_charges_amount: Number(form.other_charges_amount || 0),
        destination,
        warehouse_id: destination === "warehouse" ? warehouseId : null,
      });

      // Attach the original PDF/image to the purchase row
      if (file && created?.id) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("parent_type", "purchase");
          fd.append("parent_id", created.id);
          fd.append("category", "invoice");
          fd.append("title", `Bill · ${form.vendor}${form.invoice_number ? " · " + form.invoice_number : ""}`);
          await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (attErr) {
          devLog.warn("Attachment upload failed", attErr);
          toast.warning("Purchase saved — but bill PDF attachment failed. You can re-attach from the row.");
        }
      }

      const destLabel = destination === "warehouse"
        ? ` → Warehouse: ${warehouses.find((w) => w.id === warehouseId)?.name || ""}`
        : destination === "site"
          ? ` → Site: ${projects.find((p) => p.id === projectId)?.name || ""}`
          : "";
      toast.success(`Purchase saved · ${cleanItems.length} line${cleanItems.length === 1 ? "" : "s"}${destLabel}`);
      reset();
      setOpen(false);
      onUploaded?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      {!hideTrigger && (
        <SheetTrigger asChild>
          <Button data-testid="upload-purchase-button" variant="outline" className="rounded-sm border-slate-300">
            <UploadSimple size={16} className="mr-2" /> Upload Bill
          </Button>
        </SheetTrigger>
      )}
      <SheetContent className="w-[720px] sm:max-w-2xl lg:w-[min(920px,95vw)] lg:max-w-none overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <MagicWand size={18} className="text-[#EA580C]" /> Upload Vendor Bill (Tally / PDF)
          </SheetTitle>
        </SheetHeader>

        <div className="mt-5 space-y-5">
          {/* Shared unit datalist (referenced by every Unit input in the lines table) */}
          <PurchaseUnitDatalist />
          {!form ? (
            <div className="border-2 border-dashed border-slate-300 rounded-sm p-6 text-center hover:border-[#EA580C] transition-colors">
              <input
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); parse(f); } }}
                className="hidden"
                id="purchase-file-input"
                data-testid="purchase-slip-input"
              />
              <label htmlFor="purchase-file-input" className="cursor-pointer">
                <UploadSimple size={28} className="mx-auto text-slate-400 mb-2" />
                <div className="text-sm font-semibold text-slate-800">Click to upload your Tally vendor bill</div>
                <div className="text-xs text-slate-500 mt-1">
                  {parsing ? "Parsing… please wait" : "AI extracts vendor, invoice#, every line with HSN + GST — you review before saving."}
                </div>
                {file && !parsing && <div className="text-xs text-slate-600 mt-2 font-mono">{file.name}</div>}
              </label>
            </div>
          ) : (
            <>
              {/* Vendor + Invoice meta — extracted (iter49) */}
              <PurchaseHeaderFields
                form={form}
                setForm={setForm}
                projectId={projectId}
                setProjectId={setProjectId}
                interstate={interstate}
                setInterstate={setInterstate}
                projects={projects}
              />

              {/* Smart suggestions — appears once we recognise the vendor */}
              {suggestions.length > 0 && (
                <button
                  type="button"
                  onClick={applySuggestions}
                  data-testid="smart-prefill-btn"
                  className="w-full flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-orange-50 to-white border border-orange-200 rounded-sm text-[12.5px] text-slate-700 hover:border-[#EA580C] transition-colors group"
                >
                  <Lightning size={14} weight="fill" className="text-[#EA580C] group-hover:animate-pulse" />
                  <span className="flex-1 text-left">
                    We&apos;ve seen <b>{form.vendor}</b> before &mdash; prefill <b>{suggestions.length}</b> line{suggestions.length === 1 ? "" : "s"} from their last bill?
                  </span>
                  <span className="text-[10.5px] font-semibold uppercase tracking-widest text-[#EA580C]">Prefill</span>
                </button>
              )}

              {/* Items table — extracted (iter49) */}
              <PurchaseLinesTable
                items={form.items}
                onUpdateItem={updateItem}
                onAddItem={addItem}
                onRemoveItem={removeItem}
                onPickMaterial={(i, m) => setForm((f) => ({
                  ...f,
                  items: f.items.map((row, idx) =>
                    idx === i
                      ? {
                          ...row,
                          description: m.description,
                          hsn: m.hsn || row.hsn,
                          unit: m.unit || row.unit || "nos",
                          gst_rate: m.gst_rate || row.gst_rate || 18,
                          unit_price: m.last_unit_price || row.unit_price,
                        }
                      : row,
                  ),
                }))}
              />

              {/* Destination — where to add these items as stock */}
              <div className="border border-slate-200 rounded-sm p-3 bg-[#FFF7ED]">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowRight size={14} className="text-[#EA580C]" />
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-700">Stock Destination</div>
                </div>
                <div className="text-[11.5px] text-slate-600 mb-2">Where should these items land? (choose &ldquo;Book only&rdquo; for services / freight / taxes)</div>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setDestination("none")}
                    data-testid="dest-none-btn"
                    className={`px-2 py-1.5 text-[12px] rounded-sm border transition-colors ${destination === "none" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-700 border-slate-300 hover:border-[#EA580C]"}`}
                  >Book only</button>
                  <button
                    type="button"
                    onClick={() => setDestination("warehouse")}
                    data-testid="dest-warehouse-btn"
                    className={`px-2 py-1.5 text-[12px] rounded-sm border transition-colors ${destination === "warehouse" ? "bg-[#EA580C] text-white border-[#EA580C]" : "bg-white text-slate-700 border-slate-300 hover:border-[#EA580C]"}`}
                  >Receive at Warehouse</button>
                  <button
                    type="button"
                    onClick={() => setDestination("site")}
                    data-testid="dest-site-btn"
                    className={`px-2 py-1.5 text-[12px] rounded-sm border transition-colors ${destination === "site" ? "bg-[#EA580C] text-white border-[#EA580C]" : "bg-white text-slate-700 border-slate-300 hover:border-[#EA580C]"}`}
                  >Deliver to Site</button>
                </div>
                {destination === "warehouse" && (
                  <div className="mt-2">
                    <Select value={warehouseId} onValueChange={setWarehouseId}>
                      <SelectTrigger data-testid="dest-warehouse-select" className="h-8 text-[12.5px]"><SelectValue placeholder="Choose warehouse…" /></SelectTrigger>
                      <SelectContent>
                        {warehouses.length === 0 && <div className="px-3 py-2 text-[12px] text-slate-500">No warehouses set up yet</div>}
                        {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}{w.code ? ` · ${w.code}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {destination === "site" && !projectId && (
                  <div className="mt-2 text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded-sm">
                    ↑ Please pick a project in &ldquo;Link Project&rdquo; above &mdash; that&apos;s the site the goods land at.
                  </div>
                )}
              </div>

              {/* Totals — with per-tax breakdown */}
              <div className="flex justify-end">
                <div className="w-72 text-[13px] space-y-1 font-mono-num">
                  <div className="flex justify-between text-slate-600"><span>Subtotal</span><span data-testid="upload-purchase-subtotal">{fmtMoney(subtotal)}</span></div>
                  {interstate ? (
                    <div className="flex justify-between text-slate-600"><span>IGST</span><span>{fmtMoney(igst)}</span></div>
                  ) : (
                    <>
                      <div className="flex justify-between text-slate-600"><span>CGST</span><span>{fmtMoney(cgst)}</span></div>
                      <div className="flex justify-between text-slate-600"><span>SGST</span><span>{fmtMoney(sgst)}</span></div>
                    </>
                  )}
                  <div className="flex justify-between font-semibold text-[15px] text-[#EA580C] pt-1 border-t border-slate-200">
                    <span>Grand Total</span>
                    <span data-testid="upload-purchase-total">{fmtMoney(grand)}</span>
                  </div>
                </div>
              </div>

              {/* Additional charges (freight, insurance, etc.) — auto-post to Expenses */}
              <div className="border border-slate-200 rounded-sm bg-slate-50">
                <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Additional charges from bill footer</div>
                  <div className="text-[11px] text-slate-500">Auto-posted to Expenses on save · linked to this bill + project</div>
                </div>
                <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Field label="Freight (₹)">
                    <Input type="number" value={form.freight_amount || 0} onChange={(e) => setForm({ ...form, freight_amount: e.target.value })} className="font-mono-num text-right" data-testid="upload-freight" />
                  </Field>
                  <Field label="Insurance (₹)">
                    <Input type="number" value={form.insurance_amount || 0} onChange={(e) => setForm({ ...form, insurance_amount: e.target.value })} className="font-mono-num text-right" data-testid="upload-insurance" />
                  </Field>
                  <Field label="Packing (₹)">
                    <Input type="number" value={form.packing_amount || 0} onChange={(e) => setForm({ ...form, packing_amount: e.target.value })} className="font-mono-num text-right" />
                  </Field>
                  <Field label="Other (₹)">
                    <Input type="number" value={form.other_charges_amount || 0} onChange={(e) => setForm({ ...form, other_charges_amount: e.target.value })} className="font-mono-num text-right" />
                  </Field>
                </div>
              </div>

              {/* Notes */}
              <Field label="Notes">
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Field>

              {/* Attached invoice indicator */}
              {file && (
                <div className="flex items-center gap-2 text-[12px] bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                  <Paperclip size={13} className="text-[#EA580C]" />
                  <span className="text-slate-700 flex-1 truncate">Original invoice will be attached: <b>{file.name}</b></span>
                </div>
              )}

              {/* Warnings */}
              {!form.vendor && (
                <div className="text-[12px] bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-sm flex items-center gap-1.5">
                  <Warning size={14} weight="fill" /> Vendor name missing — please fill.
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={reset} data-testid="purchase-reupload-btn" className="rounded-sm">Re-upload</Button>
                <Button
                  onClick={save}
                  disabled={saving || !form.vendor || form.items.length === 0}
                  data-testid="save-uploaded-purchase"
                  className="flex-1 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white"
                >
                  {saving ? "Saving…" : "Save Purchase Bill"}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
